'use client';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import {
  type CreditV1CreateOrderRequest,
  type CreditV1GetOrderStateReply,
  changeSubscription as changeSubscriptionAPI,
  createOrder as createOrderAPI,
  getChangePreview,
  getOrder,
} from '@/app/apis';
import { toast } from '@/app/components/ui/use-toast';
import { API_ORIGIN, resolveApiPath, SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type {
  ChangePreview,
  ChangeSubscriptionRequest,
  ProductType,
  ServiceError,
  SubscriptionProductType,
  SubsPlanType,
} from '@/app/types';
import { getSubscribeTypeOrigin, isMembershipRevisionActive, reportEvent } from '@/app/utils';
import {
  openOrRedirect,
  trackBingPurchase,
  trackGAPurchase,
  trackMetaSubscribe,
  trackYahooPurchase,
} from '@/app/utils/client';
import { useRetentionStrategy } from '../components/shared/RetentionStrategy';
import { useBonusCode } from './use-bonus-code';
import { useClaw } from './use-claw';
import { useCredits } from './use-credits';
import { useFetch } from './use-fetch';
import { useFetchStream } from './use-fetch-stream';
import { usePaymentIntent } from './use-payment-intent';
import { useSubscription } from './use-subscription';
import { useUserInfo } from './use-user-info';

const PAY_STATE_URL = resolveApiPath('/credit/v1/orders/state');

export const usePayment = () => {
  const {
    payProduct,
    payQuantity,
    payCouponId,
    isPaidTrialPurchase,
    subscription,
    bonusCode,
    userInfo,
    updatePaymentState,
    closePayment,
    handleQrcodeTimeout,
    updateRefreshGetProducts,
    updateBonusCode,
    updateCouponSummary,
    invalidateUserQuota,
  } = useStore((state) => ({
    payProduct: state.payProduct,
    payQuantity: state.payQuantity,
    payCouponId: state.payCouponId,
    isPaidTrialPurchase: state.isPaidTrialPurchase,
    subscription: state.subscription,
    bonusCode: state.bonusCode,
    userInfo: state.userInfo,
    updatePaymentState: state.updatePaymentState,
    closePayment: state.closePayment,
    handleQrcodeTimeout: state.handleQrcodeTimeout,
    updateRefreshGetProducts: state.updateRefreshGetProducts,
    updateBonusCode: state.updateBonusCode,
    updateCouponSummary: state.updateCouponSummary,
    invalidateUserQuota: state.invalidateUserQuota,
  }));

  const { updateSubscription } = useSubscription();
  const { updateCredits } = useCredits();
  const { getUserInfo } = useUserInfo();
  const { getMyClaws } = useClaw();
  const { clearPaymentIntent } = usePaymentIntent();
  const { isEligible: isRetentionEligible } = useRetentionStrategy();
  const { resolveBonusCode } = useBonusCode();
  const shouldKeepSSERef = useRef<boolean>(false);
  const orderId = useRef<string>('');
  const t = useTranslations();

  const preValidateBonusCode = useCallback(
    (code: string | null): Promise<string | undefined> => {
      if (SITE !== 'china') return Promise.resolve(code || undefined);
      return resolveBonusCode({ forOrder: true });
    },
    [resolveBonusCode],
  );

  // 获取订单详情
  const { loading: getOrderInfoLoading, run: getOrderInfo } = useFetch((id: string) => getOrder({ path: { id } }), {
    manual: true,
    onSuccess: ({ data, error }) => {
      if (!error && data && data.status === 'success') {
        // 成功后清空福利码
        updateBonusCode(null);
        // yahoo埋点
        trackYahooPurchase(data.amount);
        // bing埋点（仅回传订阅事件）
        trackBingPurchase(data.amount, data.product_type);
        // Meta Pixel 订阅事件（仅订阅类型，金额从分转换为美元）
        if (data.product_type === 'subscription') {
          trackMetaSubscribe(data.amount / 100);
        }
        // GA4 标准 purchase 事件（含订阅、积分包、升降级、续费等所有订单）
        trackGAPurchase(
          {
            ...data,
            plan_type: data.plan_type as Exclude<SubsPlanType, 'enterprise'>,
          },
          {
            email: userInfo?.email,
            phoneNumber: userInfo?.phone,
          },
        );
        updateSubscription(); // 更新订阅
        updateCredits(); // 更新积分
        getUserInfo(); // 更新用户信息
        getMyClaws(); // 更新claws
        if (data.product_type === 'nopeak_pack' && isMembershipRevisionActive(userInfo)) {
          invalidateUserQuota();
        }
        updatePaymentState({
          payStatus: 'success',
          paymentPopupState: true,
          orderInfo: {
            ...data,
            plan_type: data.plan_type as Exclude<SubsPlanType, 'enterprise'>,
          },
        });
        return;
      }
      updatePaymentState({
        payStatus: 'failed',
      });
    },
  });

  // SSE 获取订单状态
  const { run: getOrderState, close: closeGetOrderState } = useFetchStream<{ id: string }>(PAY_STATE_URL, {
    baseURL: API_ORIGIN,
    onMessage: (str) => {
      try {
        const dataObjects = str
          .split('data:')
          .map((d) => d.trim())
          .filter((d) => d);

        const { state } = JSON.parse(dataObjects[0]) as CreditV1GetOrderStateReply & ServiceError;
        // 成功 或 关闭
        if (state === 'success' || state === 'closed') {
          shouldKeepSSERef.current = false;
          if (state === 'closed') {
            updatePaymentState({ payStatus: state });
          }
          if (state === 'success') {
            getOrderInfo(orderId.current);
          }
        } else {
          shouldKeepSSERef.current = true;
        }
      } catch (error) {
        console.error('Error_from_get_order_state: ', error);
      }
    },
    onClose: (params) => {
      if (shouldKeepSSERef.current && params) {
        getOrderState(params);
      }
    },
  });

  // 变更订阅
  const { loading: changeSubscriptionLoading, run: changeSubscription } = useFetch(
    (body: ChangeSubscriptionRequest) => changeSubscriptionAPI({ body: body }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          // 订阅变更已存在
          if (error.code === 409 && error.reason === 'SubscriptionChangeExists') {
            updatePaymentState({
              payStatus: 'subscriptionChangeExists',
            });
          } else if (error.code === 400 && error.reason === 'PaymentFailed') {
            // 支付失败
            updatePaymentState({
              payStatus: 'paymentFailed',
            });
          } else {
            updatePaymentState({
              payStatus: 'failed',
            });
          }
          return;
        }

        if (data.status === 'pending' && data.order_id) {
          orderId.current = data.order_id;
          getOrderState({ id: data.order_id }); // 获取订单状态长链接
          updatePaymentState({
            payStatus: 'pending',
            paymentInfo: data,
          });
          if (['stripe'].includes(payProduct?.pay_channel || '') && data?.redirect_url) {
            openOrRedirect(data.redirect_url);
          }
        } else if (data.status === 'success' && payProduct) {
          // 下期生效的会在这里直接成功
          const currentProduct = payProduct as SubscriptionProductType;
          updatePaymentState({
            payStatus: 'success',
            orderInfo: {
              created_at: '',
              id: data.order_id || '',
              amount: currentProduct.price,
              currency: currentProduct.currency,
              product_id: currentProduct.id,
              product_type: currentProduct.product_type || 'unspecified',
              plan_type: currentProduct.plan_type as Exclude<SubsPlanType, 'enterprise'>,
              billing_type: currentProduct.billing_period || 'unspecified',
              pay_channel: currentProduct.pay_channel,
              period_start: subscription?.next_billing_time, // 下期生效开始时间
            },
          });
        } else {
          updatePaymentState({
            payStatus: 'failed',
          });
        }
      },
    },
  );

  // 订单生成
  const { loading: createNewOrderLoading, run: createNewOrder } = useFetch(
    (body: CreditV1CreateOrderRequest) => createOrderAPI({ body: body }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          if (error.code === 400) {
            // 首期折扣 当前有未完成订单
            if (error.reason === 'OperationInProcess') {
              toast({
                type: 'warning',
                message: t('payments.payResultToast.operationInProcess'),
              });
              updatePaymentState({ payStatus: 'unspecified' });
              return;
            }
            if (error.reason === 'FieldInvalid') {
              // 各种码，优惠券的实效错误
              const fields = error.metadata?.fields || '';
              // 优惠券失效/已被使用
              if (['coupon is not unused', 'coupon is expired'].includes(fields)) {
                toast({
                  type: 'warning',
                  message: t('payments.payResultToast.couponInvalid'),
                });
                updatePaymentState({ payStatus: 'unspecified' });
                return;
              }
              if (['bonus code is invalid'].includes(fields)) {
                toast({
                  type: 'warning',
                  message: '达人合作码错误/失效',
                });
                updatePaymentState({ payStatus: 'unspecified' });
                return;
              }
            }
          }
          toast({
            type: 'warning',
            message:
              payProduct?.pay_channel === 'alipay' ? t('payments.networkError') : t('payments.getStripeUrlError'),
          });
          updatePaymentState({ payStatus: 'failed' });
          return;
        }
        updatePaymentState({ payStatus: 'pending', paymentInfo: data });
        orderId.current = data.order_id;
        getOrderState({ id: data.order_id }); // 获取订单状态长链接
        if (['stripe'].includes(payProduct?.pay_channel || '')) {
          openOrRedirect(data.redirect_url);
        }
      },
    },
  );

  // 获取变更预览
  const { loading: getChangePreviewLoading, run: fetchChangePreview } = useFetch(
    (queryData: ChangePreview) => getChangePreview({ query: queryData }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          if (error.code === 409 && error.reason === 'SubscriptionChangeExists') {
            updatePaymentState({
              payStatus: 'subscriptionChangeExists',
              paymentPopupState: true,
            });
          } else {
            toast({
              type: 'warning',
              message: t('payments.networkError'),
            });
            updatePaymentState({ payStatus: 'failed' });
          }
          updatePaymentState({
            payProduct: undefined,
          });
          return;
        }
        // 成功，进入change确认页面
        updatePaymentState({
          payStatus: 'unspecified',
          paymentPopupState: true,
          confirmedChangeData: data,
        });
      },
    },
  );

  // 仅刷新变更预览价格（弹窗已打开时，切换优惠券用）
  const { run: refreshChangePreview } = useFetch((queryData: ChangePreview) => getChangePreview({ query: queryData }), {
    manual: true,
    onSuccess: ({ data, error }) => {
      if (!error && data) {
        updatePaymentState({ confirmedChangeData: data });
      }
    },
  });

  // 创建订单的统一方法（下单前无感校验合作码，无效则不传码）
  // couponIdOverride: string = 指定使用该券；null/undefined = 不使用优惠券（不传 coupon_id）
  const createOrder = (currentProduct: ProductType, couponIdOverride?: string | null) => {
    const productPayStatus = currentProduct.product_pay_status;

    preValidateBonusCode(bonusCode).then((validatedCode) => {
      if (['continue', 'change', 'downgrade', 'upgrade'].includes(productPayStatus)) {
        const isContinue = productPayStatus === 'continue';
        const useRetentionScene = isRetentionEligible || isContinue;
        const { id, change_type, product_type, billing_period, plan_type } = currentProduct as SubscriptionProductType;
        // 仅「立即生效（immediate）」变更允许用券；end_of_period（降级、年变月等下周期生效）当场不扣费，强制原价。
        // couponIdOverride 优先（ChangeContent 之外的直接调用方）；undefined 时回退到 store 中 pay() 写入的 payCouponId
        // （Stripe 升级链路：StripeConfirmDialog 选券 → pay 写入 store → ChangeContent.clickCreateOrder() 不传券 → 这里读出来）
        const effectiveCouponOverride = couponIdOverride !== undefined ? couponIdOverride : payCouponId;
        const changeCouponId = change_type === 'immediate' ? (effectiveCouponOverride ?? undefined) : undefined;
        changeSubscription({
          product_id: id,
          subscription_id: subscription?.id,
          change_type: isContinue ? 'end_of_period' : change_type,
          bonus_code: validatedCode,
          ...(useRetentionScene && { scene: 'advance_renew_after_cancel' as const }),
          ...(changeCouponId ? { coupon_id: changeCouponId } : {}),
        });
        reportEvent('vip_change_confirm', {
          change_type: useRetentionScene ? 'advance_renew_after_cancel' : productPayStatus,
          subscribe_type: `${product_type}-${billing_period}`,
          vip_type: plan_type,
          subscribe_type_origin: getSubscribeTypeOrigin(subscription),
          vip_type_original: subscription?.plan_type || '',
        });
      } else {
        // 试用产品强制不使用优惠券
        const couponId = isPaidTrialPurchase
          ? undefined
          : couponIdOverride !== undefined
            ? (couponIdOverride ?? undefined)
            : (currentProduct?.available_coupons?.find((v) => v.is_best)?.id as string) || undefined;
        createNewOrder({
          product_id: currentProduct.id,
          coupon_id: couponId,
          bonus_code: validatedCode,
          ...(payQuantity && payQuantity > 1 ? { quantity: payQuantity } : {}),
          use_paid_trial: isPaidTrialPurchase ?? false,
        });
      }
    });
  };

  // 主要的支付方法（下单前无感校验合作码，无效则不传码）
  // couponIdOverride: string = 指定使用该券；null/undefined = 不使用优惠券（不传 coupon_id）
  const pay = (
    product: ProductType,
    options?: { quantity?: number; couponIdOverride?: string | null; isPaidTrial?: boolean },
  ) => {
    const productPayStatus = product.product_pay_status;
    const quantity = options?.quantity;
    // 持久化券选择，供变更流程跨组件使用（StripeConfirmDialog → fetchChangePreview → ChangeContent → changeSubscription）
    updatePaymentState({
      payProduct: product,
      payQuantity: quantity,
      payCouponId: options?.couponIdOverride,
      isPaidTrialPurchase: options?.isPaidTrial ?? false,
      // 固化「发起变更时是否在试用期」：变更成功后会重新拉取 subscription，
      // is_paid_trialing 可能已翻转，结果页需要的是变更前的事实
      paidTrialingAtChange: Boolean(subscription?.is_paid_trialing),
    });

    preValidateBonusCode(bonusCode).then((validatedCode) => {
      if (['continue', 'change', 'downgrade', 'upgrade'].includes(productPayStatus)) {
        const isContinue = productPayStatus === 'continue';
        const useRetentionScene = isRetentionEligible || isContinue;
        // 仅「立即生效（immediate）」变更按用户所选传券；end_of_period（降级、年变月等）强制原价，
        // 与 changeSubscription / ChangeContent 保持一致，避免预览/实付价不一致
        const previewCouponId =
          (product as SubscriptionProductType).change_type === 'immediate'
            ? (options?.couponIdOverride ?? undefined)
            : undefined;
        fetchChangePreview({
          change_type: isContinue ? 'end_of_period' : (product as SubscriptionProductType).change_type,
          product_id: product.id,
          subscription_id: subscription?.id || '',
          ...(useRetentionScene && { scene: 'advance_renew_after_cancel' as const }),
          ...(previewCouponId ? { coupon_id: previewCouponId } : {}),
        });
      } else if (product.pay_channel === 'alipay') {
        updatePaymentState({ payStatus: 'unspecified', paymentPopupState: true });
      } else if (product.pay_channel === 'stripe') {
        // 试用产品强制不使用优惠券
        const couponId = options?.isPaidTrial
          ? undefined
          : options?.couponIdOverride !== undefined
            ? (options.couponIdOverride ?? undefined) // null → undefined（不使用优惠券）；string → 使用该券
            : (product?.available_coupons?.find((v) => v.is_best)?.id as string) || undefined; // undefined → 自动套 is_best
        createNewOrder({
          product_id: product.id,
          coupon_id: couponId,
          bonus_code: validatedCode,
          ...(quantity && quantity > 1 ? { quantity } : {}),
          use_paid_trial: options?.isPaidTrial ?? false,
        });
      }
    });
    clearPaymentIntent();
  };

  // 关闭支付（包含SSE清理）
  const onClosePayment = useCallback(() => {
    // 关闭 SSE
    shouldKeepSSERef.current = false;
    closeGetOrderState();
    // 调用store的closePayment
    closePayment();
    // 不论支付成功 / 失败 / 用户主动关闭，都刷新产品列表：
    // 支付成功后已用券需从 available_coupons 中移除；支付失败或中途关闭时，
    // 服务端可能已锁定券，前端必须重新拉取，避免折扣 / 可用券数量与实际不符
    updateRefreshGetProducts(true);
    // 同步清空 couponSummary，让订阅页 Header 重新请求可用券数量
    updateCouponSummary(null);
  }, [closeGetOrderState]);

  // 二维码超时处理（包含SSE清理）
  const onQrcodeTimeout = useCallback(() => {
    // 关闭 SSE
    shouldKeepSSERef.current = false;
    closeGetOrderState();
    handleQrcodeTimeout();
  }, [closeGetOrderState]);

  // 获取loading状态
  useEffect(() => {
    if (
      payProduct &&
      (getOrderInfoLoading || changeSubscriptionLoading || createNewOrderLoading || getChangePreviewLoading)
    ) {
      updatePaymentState({
        payStatus: 'loading',
      });
    }
  }, [payProduct, getOrderInfoLoading, changeSubscriptionLoading, createNewOrderLoading, getChangePreviewLoading]);

  return {
    pay,
    createOrder,
    refreshChangePreview,
    onClosePayment,
    onQrcodeTimeout,
  };
};
